import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, scan, withLatestFrom } from 'rxjs/operators';

import { GtmEvent, ToasterService } from '@fcom/common';
import { unsubscribe } from '@fcom/core/utils';

import { getCabinFromUpgrade, getUpgradeByCabin, getUpgradeFromFlight } from '../../utils/data.utils';
import { Buyer, Cabin, Flights, FlightsV2, FlightV2, Upgrade, UpgradeProduct } from '../../api/models';
import {
  ExtendedFlightBasket,
  FlightBasket,
  PaymentChangeEvent,
  PaymentTotal,
  PaymentType,
  Stage,
  State,
  StatusState,
} from '../../interfaces';
import * as paymentUtils from '../../utils/payment.utils';
import { sortByPaymentType, UpgradePaymentService } from '../../services/upgrade-payment.service';
import {
  getBasketFlights,
  getBasketPassengerIds,
  getFlights,
  getJourneys,
  getPaymentTotal,
  getUser,
  selectStatusState,
} from '../../store/selectors';
import { BasketActions } from '../../store/actions';
import { UpgradeAnalyticsService } from '../../services/upgrade-analytics.service';

// @todo Update UpdateFlightBasketParams to new version
interface UpdateFlightBasketParams {
  flightIsDeselected: boolean;
  flight: { flightId: string; group: number; longHaul: boolean };
  upgrade: Upgrade;
  paymentType: PaymentType;
  selectedFlights: ExtendedFlightBasket[];
  cabin: Cabin;
}

@Component({
  selector: 'fin-upgrade-flights',
  templateUrl: 'upgrade-flights.component.html',
})
export class UpgradeFlightsComponent implements OnInit, OnDestroy {
  journeys$: Observable<Flights[]>;
  flights$: Observable<FlightsV2>;
  selectedPassengers$: Observable<string[]>;
  selectedFlights$: Observable<ExtendedFlightBasket[]>;
  paymentTotal$: Observable<PaymentTotal>;
  buyer$: Observable<Buyer>;
  currentStage$: Observable<StatusState>;

  paymentForm: UntypedFormGroup;
  paymentMethodChanged$ = new Subject<PaymentChangeEvent>();
  flightTabChanged$ = new Subject<{ flightId: string; cabin: Cabin }>();

  subscriptions: Subscription = new Subscription();
  public readonly Stage = Stage;

  constructor(
    private store: Store<State>,
    private toaster: ToasterService,
    private paymentService: UpgradePaymentService,
    private upgradeAnalyticsService: UpgradeAnalyticsService
  ) {}

  ngOnInit(): void {
    this.journeys$ = this.store.pipe(select(getJourneys));
    this.flights$ = this.store.pipe(select(getFlights));
    this.selectedPassengers$ = this.store.pipe(select(getBasketPassengerIds));
    this.selectedFlights$ = this.store.pipe(select(getBasketFlights));
    this.paymentTotal$ = this.store.pipe(select(getPaymentTotal));
    this.buyer$ = this.store.pipe(select(getUser));
    this.currentStage$ = this.store.pipe(select(selectStatusState));

    this.subscriptions.add(
      combineLatest([this.flights$, this.selectedFlights$]).subscribe(
        ([flights, selectedFlights]: [Flights, ExtendedFlightBasket[]]) => {
          this.constructForm(flights, selectedFlights);
        }
      )
    );

    this.subscriptions.add(
      combineLatest([this.flights$, this.paymentMethodChanged$])
        .pipe(withLatestFrom(this.selectedFlights$))
        .subscribe(([[flights, data], selectedFlights]) => {
          const flightThatChanged = flights.find((flight) => flight.segmentInfo.flightId === data.flightId);

          if (!flightThatChanged) {
            return;
          }

          const upgrade = getUpgradeFromFlight(flightThatChanged, data.upgradeProduct);
          const cabinClass = getCabinFromUpgrade(upgrade);

          this.forceSinglePaymentOptionForFlight(data.elemIndex, data.flightId, cabinClass);
          this.updateFlightBasket({
            flightIsDeselected: !data.selected,
            flight: { flightId: data.flightId, group: data.group, longHaul: data.longHaul },
            upgrade,
            paymentType: data.paymentType,
            selectedFlights,
            cabin: cabinClass,
          });
        })
    );

    this.subscriptions.add(
      this.flightTabChanged$
        .pipe(withLatestFrom(this.selectedFlights$))
        .subscribe(([flightTabChanged, selectedFlight]) => {
          const flightThatChanged = selectedFlight.find((flight) => flight.flightId === flightTabChanged.flightId);
          const controls = (
            this.paymentForm.get([flightTabChanged.flightId]).get(flightTabChanged.cabin) as UntypedFormArray
          ).controls;
          controls.forEach((control: AbstractControl): void => {
            if (flightTabChanged.cabin !== flightThatChanged?.cabin) {
              control.setValue(false);
            }
          });
        })
    );

    this.subscriptions.add(
      this.showLongHaulToast().subscribe(({ added, removed }) => {
        if (added) {
          this.toaster.addMessageToQueue({
            id: 'UPGRADE_LONGHAUL_DISCOUNT',
            contentKey: 'oneUpgrade.toaster.longhaulDiscount',
            autoClose: true,
            beneathModal: true,
          });
        }
        if (removed) {
          this.toaster.addMessageToQueue({
            id: 'UPGRADE_LONGHAUL_DISCOUNT_REMOVED',
            contentKey: 'oneUpgrade.toaster.longhaulDiscountRemoved',
            autoClose: true,
            beneathModal: true,
          });
        }
      })
    );

    this.subscriptions.add(
      this.showWaitlistToast().subscribe((hasWaitlist) => {
        if (hasWaitlist) {
          this.toaster.addMessageToQueue({
            id: 'UPGRADE_WAITLISTED',
            contentKey: 'oneUpgrade.toaster.waitlisted',
            autoClose: false,
            beneathModal: true,
          });
        } else {
          this.toaster.removeMessage('UPGRADE_WAITLISTED');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  showLongHaulToast(): Observable<{
    added: boolean;
    removed: boolean;
  }> {
    return combineLatest([this.flights$, this.selectedFlights$]).pipe(
      map(([flights, selectedFlights]) => {
        const groups = new Set(
          selectedFlights
            .filter(
              (selected) =>
                selected.flight.segmentInfo.longHaul &&
                selected.paymentType === PaymentType.points &&
                flights.find((flight) =>
                  flight.upgrades.some(
                    (f) =>
                      !flight.segmentInfo.longHaul && // short haul
                      flight.segmentInfo.group === selected.flight.segmentInfo.group && // same group
                      !!f.paymentOptions.points && // short haul has points option
                      f.paymentOptions.points.priceWithLongHaul !== undefined && // short haul has discounted points price
                      selected.productName === UpgradeProduct.EconomyToBusiness // long haul upgrade is economy-to-business
                  )
                )
            )
            .map((selected) => selected.flight.segmentInfo.group)
        );
        return Array.from(groups);
      }), // only care about longHaul paid with points where there is a shortHaul in the same group
      distinctUntilChanged(),
      scan(
        (acc, cur) => {
          return {
            prev: cur,
            added: !cur.every((group) => acc.prev.includes(group)),
            removed: !acc.prev.every((group) => cur.includes(group)),
          };
        },
        {
          prev: [],
          added: false,
          removed: false,
        }
      ),
      map(({ added, removed }) => ({ added, removed })),
      filter((x) => x.added || x.removed)
    );
  }

  showWaitlistToast(): Observable<boolean> {
    return this.selectedFlights$.pipe(
      map((flights: ExtendedFlightBasket[]) => {
        return flights
          .filter((flightBasket) => {
            return getUpgradeFromFlight(flightBasket.flight, flightBasket.productName).isWaitlist;
          })
          .map((flightBasket) => flightBasket.flight.segmentInfo.flightId);
      }),
      distinctUntilChanged((prev, cur) => {
        // When there is no waitlist in the basket mark as distinct
        if (!cur.length) {
          return false;
        }

        return cur.every((flight) => prev.includes(flight));
      }),
      map((flights) => {
        return Boolean(flights.length);
      })
    );
  }

  private constructForm(flights: FlightsV2, selectedFlights: ExtendedFlightBasket[]): void {
    const inputArrays = flights.reduce<{ [key: string]: AbstractControl }>((acc, flight) => {
      const premiumEconomyUpgrade = getUpgradeByCabin(Cabin.Ecopremium, flight.upgrades);
      const businessUpgrade = getUpgradeByCabin(Cabin.Business, flight.upgrades);

      acc[flight.segmentInfo.flightId] = new UntypedFormGroup({
        ...(premiumEconomyUpgrade && {
          [Cabin.Ecopremium]: new UntypedFormArray(
            this.generateInputFormControl(flight, selectedFlights, premiumEconomyUpgrade)
          ),
        }),
        ...(businessUpgrade && {
          [Cabin.Business]: new UntypedFormArray(
            this.generateInputFormControl(flight, selectedFlights, businessUpgrade)
          ),
        }),
      });

      return acc;
    }, {});

    if (!flights.length) {
      this.paymentForm = undefined;

      return;
    }

    if (this.paymentForm) {
      this.updatePaymentForm(flights, inputArrays);
    } else {
      this.paymentForm = new UntypedFormGroup(inputArrays);
    }
  }

  updatePaymentForm(
    flights: FlightsV2,
    inputArrays: {
      [key: string]: AbstractControl;
    }
  ): void {
    flights.forEach((flight) => {
      const flightGroup = this.paymentForm.get(flight.segmentInfo.flightId);

      [Cabin.Ecopremium, Cabin.Business].forEach((cabin) => {
        const upgrade = getUpgradeByCabin(cabin, flight.upgrades);

        if (upgrade && flightGroup) {
          const cabinArray = inputArrays[flight.segmentInfo.flightId].get(cabin) as UntypedFormArray;

          for (let i = 0; i < cabinArray.length; i++) {
            const value = cabinArray.get(`${i}`).value;
            const disabled = cabinArray.get(`${i}`).disabled;

            flightGroup.get(cabin).get(`${i}`).setValue(value);
            flightGroup.get(cabin).get(`${i}`)[disabled ? 'disable' : 'enable']();
          }
        }
      });
    });
  }

  generateInputFormControl(
    flight: FlightV2,
    selectedFlights: ExtendedFlightBasket[],
    upgrade: Upgrade
  ): AbstractControl[] {
    const matchingFlight = selectedFlights.find((selected) => selected.flightId === flight.segmentInfo.flightId);

    const longHaulDiscount = !flight.segmentInfo.longHaul
      ? paymentUtils.isLongHaulUpgraded(selectedFlights, flight.segmentInfo.group)
      : false;

    return (Object.keys(upgrade?.paymentOptions || {}) as unknown as PaymentType[])
      .filter((paymentType: PaymentType) => !!upgrade?.paymentOptions[paymentType])
      .sort(sortByPaymentType)
      .map((paymentName: PaymentType) => {
        const paymentIsAlreadySelected = matchingFlight ? paymentName === matchingFlight.paymentType : false;
        const cabin = getCabinFromUpgrade(upgrade);

        const { selected, disabled } = this.selectFreeOption(
          longHaulDiscount,
          paymentIsAlreadySelected,
          paymentName,
          flight,
          selectedFlights,
          upgrade,
          cabin
        );

        return new UntypedFormControl({
          value: selected,
          disabled: disabled || this.isPaymentOptionDisabled(selectedFlights, flight, paymentName, upgrade),
        });
      });
  }

  forceSinglePaymentOptionForFlight(elemIndex: number, flightId: string, cabin: Cabin): void {
    const controls = (this.paymentForm.get(flightId).get(cabin) as UntypedFormArray).controls;

    controls.forEach((control: AbstractControl, index: number): void => {
      if (elemIndex !== index) {
        control.setValue(false);
      }
    });
  }

  /**
   * Toggle flight in basket
   */
  updateFlightBasket(params: UpdateFlightBasketParams): void {
    const item: FlightBasket = {
      flightId: params.flight.flightId,
      productName: params.upgrade.upgradeProductName,
      paymentType: params.paymentType,
      cabin: params.cabin,
    };

    const flightInBasket = params.selectedFlights.find((selected) => selected.flightId === params.flight.flightId);

    if (params.flightIsDeselected) {
      this.store.dispatch(BasketActions.removeFlight({ flightId: params.flight.flightId }));
      this.upgradeAnalyticsService.sendAddRemoveEvent(GtmEvent.REMOVE_FROM_CART, item);
    } else if (flightInBasket) {
      this.store.dispatch(BasketActions.updateFlight({ flight: item }));

      if (flightInBasket.productName !== item.productName) {
        this.upgradeAnalyticsService.sendAddRemoveEvent(GtmEvent.REMOVE_FROM_CART, flightInBasket);
        this.upgradeAnalyticsService.sendAddRemoveEvent(GtmEvent.ADD_TO_CART, item);
      }
    } else {
      this.store.dispatch(BasketActions.addFlightToBasket({ flight: item }));
      this.upgradeAnalyticsService.sendAddRemoveEvent(GtmEvent.ADD_TO_CART, item);
    }
  }

  /**
   * Check if payment option should be disabled
   *
   * @param paxCount number of passengers to upgrade
   * @param flightBasket array of selected flights
   * @param flight Current flight details
   * @param paymentType type of payment
   * @param buyer The buyers balance
   */
  private isPaymentOptionDisabled(
    flightBasket: ExtendedFlightBasket[],
    flight: FlightV2,
    paymentType: PaymentType,
    upgrade: Upgrade
  ) {
    if (flightBasket.find((x) => x.flightId === flight.segmentInfo.flightId && x.paymentType === paymentType)) {
      /* if already selected return false to allow deselecting */
      return false;
    }

    // if user can't pay then return true
    if (this.paymentService.insufficientPayment(flight, upgrade.upgradeProductName, paymentType)) {
      return true;
    }

    /**
     * if basket empty or only flight in basket is this flight,
     * then return false to allow swapping payment of current flight
     */
    const onlyThisFlightSelected =
      flightBasket.length === 1 && flightBasket[0].flightId === flight.segmentInfo.flightId;

    if (!flightBasket.length || onlyThisFlightSelected) {
      return false;
    }

    /**
     * if incompatible payment then return true
     */
    const selectedPayments = flightBasket.map((x) => x.paymentType);

    if (paymentType === PaymentType.money) {
      return (
        selectedPayments.includes(PaymentType.points) ||
        selectedPayments.includes(PaymentType.EUROPE_UPGRADE) ||
        selectedPayments.includes(PaymentType.LONG_HAUL_UPGRADE) ||
        selectedPayments.includes(PaymentType.UPGRADE_BENEFIT)
      );
    } else {
      return selectedPayments.includes(PaymentType.money);
    }
  }

  /**
   * Check if flight should be disabled or selected due to long haul discount
   *
   * @param longHaulDiscount is there a long haul discount that can be applied to this flight
   * @param isSelected is the option already selected
   * @param paymentType What Payment Type is this option?
   * @param flight Flight details
   * @param selectedFlights Flights in basket
   */
  private selectFreeOption(
    longHaulDiscount: boolean,
    isSelected: boolean,
    paymentType: PaymentType,
    flight: FlightV2,
    selectedFlights: ExtendedFlightBasket[],
    upgrade: Upgrade,
    cabin: Cabin
  ): {
    selected: boolean;
    disabled: boolean;
  } {
    if (
      longHaulDiscount &&
      !!upgrade?.paymentOptions.points &&
      upgrade?.paymentOptions.points.priceWithLongHaul !== undefined &&
      upgrade?.paymentOptions.points.priceWithLongHaul === 0
    ) {
      if (paymentType === PaymentType.points) {
        if (!isSelected) {
          this.updateFlightBasket({
            flightIsDeselected: false,
            flight: {
              flightId: flight.segmentInfo.flightId,
              group: flight.segmentInfo.group,
              longHaul: flight.segmentInfo.longHaul,
            },
            upgrade,
            paymentType: PaymentType.points,
            selectedFlights,
            cabin,
          });
        }
        return {
          selected: true,
          disabled: true,
        };
      } else {
        return {
          selected: false,
          disabled: true,
        };
      }
    }
    return {
      selected: isSelected,
      disabled: false,
    };
  }
}
